import type { BatonEdge, BatonNode } from "./model.js";

/**
 * What a hand-drawn connection means. Agent → agent (right handle to left handle) is a
 * conversation wire: the two agents talk to each other. Repository → agent (bottom handle
 * to the agent's top handle) assigns the viewer's own agent to that repository. Every
 * other combination is refused before React Flow draws anything.
 */
export type ConnectionIntent =
  | { kind: "wire"; sourceAgentId: string; targetAgentId: string }
  | { kind: "assignment"; agentId: string; repositoryId: string }
  | {
    kind: "invalid";
    reason: "same-node" | "not-owned" | "duplicate-wire" | "duplicate-assignment" | "direction" | "unknown";
  };

export interface ConnectionCandidate {
  source: string | null;
  target: string | null;
  sourceHandle?: string | null | undefined;
  targetHandle?: string | null | undefined;
}

export function classifyConnection(
  candidate: ConnectionCandidate,
  nodes: readonly BatonNode[],
  edges: readonly BatonEdge[],
  viewer: { userId: string },
): ConnectionIntent {
  if (!candidate.source || !candidate.target) return { kind: "invalid", reason: "unknown" };
  if (candidate.source === candidate.target) return { kind: "invalid", reason: "same-node" };
  const source = nodes.find((node) => node.id === candidate.source);
  const target = nodes.find((node) => node.id === candidate.target);
  if (!source || !target) return { kind: "invalid", reason: "unknown" };
  const sourceHandle = candidate.sourceHandle ?? "out";
  const targetHandle = candidate.targetHandle ?? "in";

  if (source.data.kind === "agent" && target.data.kind === "agent") {
    if (sourceHandle !== "out" || targetHandle !== "in") return { kind: "invalid", reason: "direction" };
    const pair = new Set([source.data.agentId, target.data.agentId]);
    const duplicate = edges.some((edge) => edge.data?.relation === "wire"
      && edge.data.sourceAgentId && edge.data.targetAgentId
      && pair.has(edge.data.sourceAgentId) && pair.has(edge.data.targetAgentId));
    if (duplicate) return { kind: "invalid", reason: "duplicate-wire" };
    return { kind: "wire", sourceAgentId: source.data.agentId, targetAgentId: target.data.agentId };
  }

  if (source.data.kind === "repository" && target.data.kind === "agent") {
    if (sourceHandle !== "out" || targetHandle !== "repo") return { kind: "invalid", reason: "direction" };
    if (target.data.ownerUserId !== viewer.userId) return { kind: "invalid", reason: "not-owned" };
    if (target.data.repositoryIds.includes(source.data.repositoryId)) return { kind: "invalid", reason: "duplicate-assignment" };
    return { kind: "assignment", agentId: target.data.agentId, repositoryId: source.data.repositoryId };
  }

  return { kind: "invalid", reason: "direction" };
}

export function isValidConnection(
  candidate: ConnectionCandidate,
  nodes: readonly BatonNode[],
  edges: readonly BatonEdge[],
  viewer: { userId: string },
): boolean {
  return classifyConnection(candidate, nodes, edges, viewer).kind !== "invalid";
}
